import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import AdminHeader from '../../components/AdminHeader';
import './ManageFoodItems.css';

// Données fictives pour la démonstration
const demoRestaurants = [
  { id: 1, name: "Le Gourmet", logo: "/images/restaurants/le-gourmet.jpg" },
  { id: 2, name: "Pizza Express", logo: "/images/restaurants/pizza-express.jpg" },
  { id: 3, name: "Sushi Master", logo: "/images/restaurants/sushi-master.jpg" },
];

const demoFoodItems = {
  1: [
    { id: 101, name: "Filet Mignon", image: "/images/food/filet-mignon.jpg", price: 28.50, description: "Filet mignon de bœuf, sauce au poivre, pommes de terre sautées" },
    { id: 102, name: "Risotto aux Cèpes", image: "/images/food/risotto.jpg", price: 18.90, description: "Risotto crémeux aux cèpes et parmesan" },
    { id: 103, name: "Crème Brûlée", image: "/images/food/creme-brulee.jpg", price: 8.50, description: "Crème brûlée à la vanille de Madagascar" },
  ],
  2: [
    { id: 201, name: "Pizza Margherita", image: "/images/food/margherita.jpg", price: 12.90, description: "Tomate, mozzarella, basilic frais" },
    { id: 202, name: "Pizza Quattro Formaggi", image: "/images/food/quattro-formaggi.jpg", price: 14.90, description: "Mozzarella, gorgonzola, parmesan, chèvre" },
    { id: 203, name: "Tiramisu", image: "/images/food/tiramisu.jpg", price: 7.50, description: "Tiramisu traditionnel au café et mascarpone" },
  ],
  3: [
    { id: 301, name: "Sushi Mix", image: "/images/food/sushi-mix.jpg", price: 22.90, description: "Assortiment de 12 pièces: nigiri, maki, california" },
    { id: 302, name: "Ramen Tonkotsu", image: "/images/food/ramen.jpg", price: 15.90, description: "Ramen au porc, œuf mollet, champignons shiitake" },
    { id: 303, name: "Mochi Glacé", image: "/images/food/mochi.jpg", price: 6.90, description: "Assortiment de 3 mochis glacés: matcha, mangue, sésame noir" },
  ],
};

const ManageFoodItems = () => {
  const { restaurantId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [restaurant, setRestaurant] = useState(null);
  const [foodItems, setFoodItems] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    image: null
  });
  
  // Simuler un chargement de données depuis une API
  useEffect(() => {
    // Dans un cas réel, vous feriez un appel API ici
    // fetch(`/api/admin/restaurants/${restaurantId}`)
    //   .then(response => response.json())
    //   .then(data => setRestaurant(data));
    
    // fetch(`/api/admin/restaurants/${restaurantId}/items`)
    //   .then(response => response.json())
    //   .then(data => setFoodItems(data));
    
    const id = parseInt(restaurantId);
    setRestaurant(demoRestaurants.find(r => r.id === id) || null);
    setFoodItems(demoFoodItems[id] || []);
  }, [restaurantId]);
  
  // Filtrer les plats selon le terme de recherche
  const filteredItems = foodItems.filter(item => 
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    item.description.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleAddItem = () => {
    setFormData({
      name: '',
      description: '',
      price: '',
      image: null
    });
    setShowAddModal(true);
  };
  
  const handleEditItem = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price,
      image: null // We'd normally set this to the existing image URL
    });
    setShowEditModal(true);
  };
  
  const handleDeleteItem = (itemId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce plat ?")) {
      setFoodItems(foodItems.filter(item => item.id !== itemId));
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleFileChange = (e) => {
    setFormData({
      ...formData,
      image: e.target.files[0]
    });
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (showAddModal) {
      // Add new food item logic
      const newItem = {
        id: Date.now(), // Using timestamp as a simple unique ID
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        image: formData.image ? URL.createObjectURL(formData.image) : "/images/food/placeholder.jpg"
      };
      
      setFoodItems([...foodItems, newItem]);
      setShowAddModal(false);
    } else if (showEditModal && selectedItem) {
      // Edit food item logic
      setFoodItems(foodItems.map(item => {
        if (item.id === selectedItem.id) {
          return {
            ...item,
            name: formData.name,
            description: formData.description,
            price: parseFloat(formData.price),
            image: formData.image ? URL.createObjectURL(formData.image) : item.image
          };
        }
        return item;
      }));
      
      setShowEditModal(false);
      setSelectedItem(null);
    }
  };
  
  const closeModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setSelectedItem(null);
  };

  if (!restaurant) {
    return (
      <div className="admin-dashboard">
        <AdminSidebar />
        <div className="dashboard-content">
          <AdminHeader title="Restaurant non trouvé" />
          <div className="not-found-message">
            <p>Le restaurant demandé n'existe pas ou a été supprimé.</p>
            <Link to="/admin/restaurants" className="back-link">Retour à la liste des restaurants</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <AdminSidebar />
      <div className="dashboard-content">
        <AdminHeader title={`Plats du restaurant: ${restaurant.name}`} />
        
        <div className="food-items-container">
          <div className="action-bar">
            <div className="restaurant-info">
              <div className="restaurant-logo">
                <img src={restaurant.logo} alt={restaurant.name} />
              </div>
              <div className="restaurant-name">
                <h2>{restaurant.name}</h2>
                <Link to="/admin/restaurants" className="back-link">Retour à la liste des restaurants</Link>
              </div>
            </div>
            
            <div className="search-add-section">
              <div className="search-box">
                <input 
                  type="text" 
                  placeholder="Rechercher un plat..." 
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="search-button">Rechercher</button>
              </div>
              
              <button className="add-item-button" onClick={handleAddItem}>
                Ajouter un Plat
              </button>
            </div>
          </div>
          
          <div className="food-items-grid">
            {filteredItems.length === 0 ? (
              <div className="no-items-message">
                <p>Aucun plat trouvé. Ajoutez des plats à ce restaurant.</p>
              </div>
            ) : (
              filteredItems.map(item => (
                <div key={item.id} className="food-item-card">
                  <div className="food-item-image">
                    <img src={item.image} alt={item.name} />
                  </div>
                  
                  <div className="food-item-details">
                    <h3>{item.name}</h3>
                    <p className="food-item-description">{item.description}</p>
                    <p className="food-item-price">{item.price.toFixed(2)} €</p>
                  </div>
                  
                  <div className="food-item-actions">
                    <button className="action-button edit-button" onClick={() => handleEditItem(item)}>
                      Modifier
                    </button>
                    <button className="action-button delete-button" onClick={() => handleDeleteItem(item.id)}>
                      Supprimer
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Modal pour ajouter/modifier un plat */}
          {(showAddModal || showEditModal) && (
            <div className="modal-backdrop">
              <div className="food-item-modal">
                <button className="close-modal" onClick={closeModal}>×</button>
                <h2>{showAddModal ? 'Ajouter un Plat' : 'Modifier le Plat'}</h2>
                
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label htmlFor="name">Nom du Plat</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name" 
                      value={formData.name} 
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="description">Description</label>
                    <textarea 
                      id="description" 
                      name="description" 
                      value={formData.description} 
                      onChange={handleInputChange}
                      required
                    ></textarea>
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="price">Prix (€)</label>
                    <input 
                      type="number" 
                      id="price" 
                      name="price" 
                      min="0" 
                      step="0.01" 
                      value={formData.price} 
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="image">Image</label>
                    <input 
                      type="file" 
                      id="image" 
                      name="image" 
                      accept="image/*" 
                      onChange={handleFileChange}
                      required={showAddModal} 
                    />
                    {showEditModal && <p className="hint">Laissez vide pour conserver l'image actuelle</p>}
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" className="cancel-button" onClick={closeModal}>Annuler</button>
                    <button type="submit" className="submit-button">
                      {showAddModal ? 'Ajouter' : 'Enregistrer les modifications'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageFoodItems;